import { fold, nameKey, nearness } from './normalize'
import { compareNames } from './locale'

// Anything with a name: list items, or the household's past names
// (lib/catalog.ts).
type Named = { name: string }

// An entry with its normalised forms computed once, so a keystroke doesn't
// re-stem the whole list. QuickAdd (components/list) builds the index in a useMemo keyed on
// its entries and hands it to matchItems on every change of the query.
export interface Indexed<T extends Named> {
  item: T
  key: string
  folded: string
}

export function indexItems<T extends Named>(items: T[]): Indexed<T>[] {
  return items.map(item => ({ item, key: nameKey(item.name), folded: fold(item.name) }))
}

// Ranks exact (normalised) match first, then prefix, then a word starting with
// the query, then substring, then a near miss — so typing "äpp" surfaces
// "Äpple" above an unrelated item that merely contains "äpp", and "mjolk"
// finds "Mjölk" on a keyboard without the ö. Near misses need four letters to
// go on, so two typed letters don't fuzz the whole list. Within a rank names
// are A–Z, or in the order given with `keepOrder` (From before comes most
// bought first, lib/catalog.ts).
export interface MatchOptions {
  keepOrder?: boolean
}

export function matchItems<T extends Named>(items: Indexed<T>[], query: string, locale: string, limit?: number, opts?: MatchOptions): T[]
export function matchItems<T extends Named>(items: T[], query: string, locale: string, limit?: number, opts?: MatchOptions): T[]
export function matchItems<T extends Named>(
  items: T[] | Indexed<T>[],
  query: string,
  locale: string,
  limit = 5,
  opts: MatchOptions = {},
): T[] {
  return rankedMatches(items, query, locale, limit, opts).map(h => h.item)
}

/** A match with how it matched: `rank` as above (0 = the same name, 4 = a near miss), `near` its distance. */
export interface Ranked<T> {
  item: T
  rank: number
  near: number | null
}

/** matchItems with the ranks, for deciding which matches are sure enough to use unasked. */
export function rankedMatches<T extends Named>(
  items: T[] | Indexed<T>[], query: string, locale: string, limit = 5, opts: MatchOptions = {},
): Ranked<T>[] {
  const qFold = fold(query)
  if (!qFold) return []
  const qKey = nameKey(query)
  const index = isIndexed(items) ? items : indexItems(items as T[])
  const compare = compareNames(locale)
  return index
    .map((entry, at) => ({ entry, at, ...rankOf(entry, qKey, qFold) }))
    .filter(x => x.rank !== -1)
    .sort((a, b) => a.rank - b.rank || (opts.keepOrder ? a.at - b.at : compare(a.entry.item.name, b.entry.item.name)))
    .slice(0, limit)
    .map(s => ({ item: s.entry.item, rank: s.rank, near: s.near }))
}

/**
 * Sure enough to rename by itself: the same name (EN↔SV and spelling aside)
 * or one slip away — never a name that merely starts with or contains the
 * other ("pasta" is not "pastasås"). The rest are suggestions to tap.
 */
export const isSureMatch = (m: Pick<Ranked<unknown>, 'rank' | 'near'>) =>
  m.rank === 0 || (m.rank === RANK_NEAR && m.near !== null && m.near <= 1)

const RANK_NEAR = 4

function rankOf({ key, folded }: { key: string; folded: string }, qKey: string, qFold: string): { rank: number; near: number | null } {
  if (key && key === qKey) return { rank: 0, near: 0 }
  if (folded.startsWith(qFold)) return { rank: 1, near: null }
  if (folded.split(' ').some(t => t.startsWith(qFold))) return { rank: 2, near: null }
  if (folded.includes(qFold)) return { rank: 3, near: null }
  const near = qFold.length >= 4 ? nearness(key, qKey) : null
  if (near !== null) return { rank: RANK_NEAR, near }
  return { rank: -1, near: null }
}

function isIndexed<T extends Named>(items: T[] | Indexed<T>[]): items is Indexed<T>[] {
  return items.length > 0 && 'folded' in items[0] && 'item' in items[0]
}
